/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphCosmosPageData } from "@cosmos/hygraph-data";
import { Locale, CosmosPageAgreementFragment, CosmosPageAgreementMetadataQuery } from "@cosmos/hygraph-schema";
import { getHygraphLocale, getSlugParam } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";
import { Metadata } from "next";

import { getHeroAndSectionsDetails, CosmosPageMetadata } from "../common";

export const getAgreementPageData = async (
	locale: string = Locale.En,
	slug: string,
	lang: string,
	previewParam?: string | string[],
	endpoint?: string,
): Promise<CosmosPageAgreementFragment> => {
	if (!slug) {
		throw new Error("Missing slug");
	}
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	const resp = await getHygraphCosmosPageData(
		"CosmosPageAgreementEntities",
		{
			locale: [locale, Locale.En] as Locale[],
			slug,
		},
		preview,
		endpoint,
	);

	if (!resp?.cosmosPageAgreement) {
		throw new Error("No such data");
	}

	const { hero, refSections } = await getHeroAndSectionsDetails(
		resp?.cosmosPageAgreement?.hero,
		resp?.cosmosPageAgreement?.refSections,
		{
			slug,
			locale,
			lang,
			preview,
			endpoint,
		},
	);

	const pageData: CosmosPageAgreementFragment = {
		...resp.cosmosPageAgreement,
		createdAt: resp.cosmosPageAgreement.createdAt,
		updatedAt: resp.cosmosPageAgreement.updatedAt,
		hero,
		refSections,
	};

	return pageData;
};

const getAgreementPageMetadata = async (
	locale: string = Locale.En,
	slug: string,
	previewParam?: string | string[],
	endpoint?: string,
): Promise<CosmosPageAgreementMetadataQuery> => {
	if (!slug) {
		throw new Error("Missing slug");
	}
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	const pageData = await getHygraphCosmosPageData(
		"CosmosPageAgreementMetadata",
		{
			locale: [locale, Locale.En] as Locale[],
			slug,
		},
		preview,
		endpoint,
	);

	return pageData;
};

export async function cosmosPageAgreementMetadata({
	slug: rootSlug = "",
	params,
	searchParams,
	hygraphLocales,
	getAppMetadata,
	hygraphEndpoint,
}: CosmosPageMetadata): Promise<Metadata> {
	const slugParam = getSlugParam(params);
	const slug = `${rootSlug}${slugParam}`;
	const locale = params.lang;
	let pageData: CosmosPageAgreementMetadataQuery = {};
	try {
		pageData = await getAgreementPageMetadata(
			getHygraphLocale(hygraphLocales, locale),
			slug,
			searchParams.preview,
			hygraphEndpoint,
		);
	} catch (e) {
		console.error("Failed to get cosmosPageAgreementMetadata()", { error: isError(e) && e.message });
	}
	const metaTags = pageData.cosmosPageAgreement?.metaTags || {};

	return getAppMetadata(metaTags, slug, locale);
}
